import { ReviewSearchParamsDto } from '@/contracts/marketplace/reviews';
import { createClientLogger, logClientError } from '@/client/logging/client-logger';
import { isRequestCanceled } from '@/client/error-handling/is-request-canceled';
import { Sort } from '@/types/sort.type';
import { useLayoutEffect } from 'react';

const reviewsRefetchLogger = createClientLogger({
    scope: 'reviews-refetch',
});

type UseReviewsClientPageRefetchEffectArgs = {
    page: number;
    reviewsOnPage: number;
    sort: Sort;
    filters: ReviewSearchParamsDto;
    refetchReviews: (abort?: AbortSignal) => Promise<void>;
};

export const useReviewsClientPageRefetchEffect = ({
    page,
    reviewsOnPage,
    sort,
    filters,
    refetchReviews,
}: UseReviewsClientPageRefetchEffectArgs) => {
    useLayoutEffect(() => {
        const controller = new AbortController();

        // Start the refetch before first paint so an immediate filter click cannot be overwritten
        // by a stale mount-time refetch that starts later.
        void refetchReviews(controller.signal).catch((error) => {
            if (controller.signal.aborted) {
                return;
            }

            if (isRequestCanceled(error)) {
                return;
            }

            logClientError({
                logger: reviewsRefetchLogger,
                message: '[client.reviews-page.initial-refetch] Failed to perform initial reviews page refetch.',
                error,
                context: {
                    page,
                    reviewsOnPage,
                    filters,
                    sort,
                },
            });
        });

        return () => {
            controller.abort();
        };
    }, [filters, page, refetchReviews, reviewsOnPage, sort.sortKey, sort.sortOrder]);
};
